import {AfterViewInit, Component, EventEmitter, Input, Output} from "@angular/core";
import {OrderFees} from "../../../../../shared/backend-api.types";
import {FormBuilder, Validators} from "@angular/forms";
import {orderFeesFormFields} from "../order-fees.form";
import {numberValidator} from "../../../../../services/utils/form.validators";

@Component({
    selector: "rc-order-fees-form",
    template: `
    <form [formGroup]="orderFeesForm">
      <ion-grid>
     
        <ion-row>
          <ion-col offset-xs="0" offset-lg="1.5" size-xs="12" size-lg="9">
            <ion-item lines="none">
              <ion-input
                label="Cost per delivery above limit ($):"
                labelPlacement="stacked"
                type="number"
                [formControlName]="orderFeesFormFields.feePerDeliveryInDollars"></ion-input>
            </ion-item>
          </ion-col>
        </ion-row>

        <ion-row>
          <ion-col offset-xs="0" offset-lg="1.5" size-xs="12" size-lg="9">
            <ion-item lines="none">
              <ion-input
                label="Cost per pickup above limit ($):"
                labelPlacement="stacked"
                type="number"
                [formControlName]="orderFeesFormFields.feePerPickupInDollars"></ion-input>
            </ion-item>
          </ion-col>
        </ion-row>

        <ion-row>
          <ion-col offset-xs="0" offset-lg="1.5" size-xs="12" size-lg="9">
            <ion-item lines="none">
              <ion-input
                label="Cost per base to base delivery ($):"
                labelPlacement="stacked"
                type="number"
                [formControlName]="orderFeesFormFields.baseToBaseFeeInDollars"></ion-input>
            </ion-item>
          </ion-col>
        </ion-row>

        <ion-row>
          <ion-col offset-xs="0" offset-lg="1.5" size-xs="12" size-lg="9">
            <ion-item lines="none">
              <ion-input
                label="Free deliveries per season:"
                labelPlacement="stacked"
                type="number"
                [formControlName]="orderFeesFormFields.freeLocalDeliveriesPerSeason"></ion-input>
            </ion-item>
          </ion-col>
        </ion-row>

        <ion-row>
          <ion-col offset-xs="0" offset-lg="1.5" size-xs="12" size-lg="9">
            <ion-item lines="none">
              <ion-input
                label="Free pickups per season:"
                labelPlacement="stacked"
                type="number"
                [formControlName]="orderFeesFormFields.freeLocalPickupsPerSeason"></ion-input>
            </ion-item>
          </ion-col>
        </ion-row>

        <ion-row>
          <ion-col offset-xs="0" offset-lg="1.5" size-xs="12" size-lg="9">
            <ion-item lines="none">
              <ion-label position="stacked">Fees are multiplied by the number of items in the order:</ion-label>
              <ion-checkbox [formControlName]="orderFeesFormFields.feeAppliesPerItem"></ion-checkbox>
            </ion-item>
          </ion-col>
        </ion-row>

        <ion-row class="ion-margin-top">
          <ion-col size-xs="12" size-md="4" offset-md="4">
            <ion-button class="full-width" color="secondary" [disabled]="!orderFeesForm.valid" (click)="save()">
              <ion-text>Save</ion-text>
            </ion-button>
          </ion-col>
        </ion-row>

        <ion-row class="ion-margin-top">
          <ion-col offset-xs="0" offset-lg="1.5" size-xs="12" size-lg="9">
            <ion-text class="example-text">
              For example, for a customer requesting orders containing
              <span class="example-highlight-text"> two </span>
              items

              <br />

              <li>
                The first
                <span class="example-highlight-text">
                   {{ this.orderFeesForm.value[orderFeesFormFields.freeLocalDeliveriesPerSeason] }} 
                </span>
                deliveries will be free
              </li>
              <li>
                The first
                <span class="example-highlight-text">
                   {{ this.orderFeesForm.value[orderFeesFormFields.freeLocalPickupsPerSeason] }} 
                </span>
                pickups will be free be free
              </li>

              <br />

              Once those limits are reached the customer will be charged the following fees:
              <li>
                Additional deliveries will cost
                <span class="example-highlight-text"> $ {{ additionalDeliveryExampleFees }} </span>
              </li>
              <li>
                Additional pickups will cost
                <span class="example-highlight-text"> $ {{ additionalPickupExampleFees }} </span>
              </li>
              <li>
                A full order will consequently cost
                <span class="example-highlight-text"> $ {{ additionalOrderExampleFees }} </span>
              </li>

              <br />

              Base to base deliveries will cost
              <span class="example-highlight-text"> $ {{ baseToBaseOrderExampleFees }} </span>
            </ion-text>
          </ion-col>
        </ion-row>
      </ion-grid>
    </form>
  `,
    styleUrls: ["./order-fees-form.component.scss"],
    standalone: false
})
export class OrderFeesFormComponent implements AfterViewInit {
  public additionalDeliveryExampleFees: number = 0;
  public additionalOrderExampleFees: number = 0;
  public additionalPickupExampleFees: number = 0;
  public baseToBaseOrderExampleFees: number = 0;
  @Input() initialFees: OrderFees;
  @Output() onSave = new EventEmitter<OrderFees>();
  public orderFeesForm = this.formBuilder.group({
    [orderFeesFormFields.baseToBaseFeeInDollars]: [0, [Validators.required, numberValidator, Validators.min(0)]],
    [orderFeesFormFields.feeAppliesPerItem]: [false, [Validators.required]],
    [orderFeesFormFields.feePerDeliveryInDollars]: [0, [Validators.required, numberValidator, Validators.min(0)]],
    [orderFeesFormFields.feePerPickupInDollars]: [0, [Validators.required, numberValidator, Validators.min(0)]],
    [orderFeesFormFields.freeLocalDeliveriesPerSeason]: [0, [Validators.required, numberValidator, Validators.min(0)]],
    [orderFeesFormFields.freeLocalPickupsPerSeason]: [0, [Validators.required, numberValidator, Validators.min(0)]],
  });
  protected readonly orderFeesFormFields = orderFeesFormFields;

  constructor(private readonly formBuilder: FormBuilder) {}

  public ngAfterViewInit(): void {
    this.orderFeesForm.valueChanges.subscribe(() => {
      this.computeExampleFees();
    });

    if (this.initialFees) {
      setTimeout(() => this.populateForm(this.initialFees));
    }
  }

  public save(): void {
    const orderFees = this.orderFeesForm.value;
    this.onSave.emit({
      baseToBaseFeeInCents: Number(orderFees[orderFeesFormFields.baseToBaseFeeInDollars]) * 100,
      feeAppliesPerItem: Boolean(orderFees[orderFeesFormFields.feeAppliesPerItem]),
      feePerDeliveryInCents: Number(orderFees[orderFeesFormFields.feePerDeliveryInDollars]) * 100,
      feePerPickupInCents: Number(orderFees[orderFeesFormFields.feePerPickupInDollars]) * 100,
      freeLocalDeliveriesPerSeason: Number(orderFees[orderFeesFormFields.freeLocalDeliveriesPerSeason]),
      freeLocalPickupsPerSeason: Number(orderFees[orderFeesFormFields.freeLocalPickupsPerSeason]),
    });
  }

  private populateForm(fees: OrderFees): void {
    const baseToBaseFeeInDollars = (fees.baseToBaseFeeInCents ?? 0) / 100;
    const feePerDeliveryInDollars = (fees.feePerDeliveryInCents ?? 0) / 100;
    const feePerPickupInDollars = (fees.feePerPickupInCents ?? 0) / 100;

    this.orderFeesForm.get(orderFeesFormFields.baseToBaseFeeInDollars)?.setValue(baseToBaseFeeInDollars);
    this.orderFeesForm.get(orderFeesFormFields.feeAppliesPerItem)?.setValue(fees.feeAppliesPerItem ?? false);
    this.orderFeesForm.get(orderFeesFormFields.feePerDeliveryInDollars)?.setValue(feePerDeliveryInDollars);
    this.orderFeesForm.get(orderFeesFormFields.feePerPickupInDollars)?.setValue(feePerPickupInDollars);
    this.orderFeesForm.get(orderFeesFormFields.freeLocalDeliveriesPerSeason)?.setValue(fees.freeLocalDeliveriesPerSeason ?? 0);
    this.orderFeesForm.get(orderFeesFormFields.freeLocalPickupsPerSeason)?.setValue(fees.freeLocalPickupsPerSeason ?? 0);

    this.orderFeesForm.updateValueAndValidity();
    this.computeExampleFees();
  }

  private computeExampleFees(): void {
    const feePerBaseToBaseDeliveryInDollars = Number(
      this.orderFeesForm.value[orderFeesFormFields.baseToBaseFeeInDollars],
    );
    const feePerDeliveryInDollars = Number(this.orderFeesForm.value[orderFeesFormFields.feePerDeliveryInDollars]);
    const feePerPickupInDollars = Number(this.orderFeesForm.value[orderFeesFormFields.feePerPickupInDollars]);
    const exampleForTwoItems = 2;
    const multiplier = this.orderFeesForm.value[orderFeesFormFields.feeAppliesPerItem] ? exampleForTwoItems : 1;

    this.additionalDeliveryExampleFees = feePerDeliveryInDollars * multiplier;
    this.additionalPickupExampleFees = feePerPickupInDollars * multiplier;
    this.additionalOrderExampleFees = this.additionalDeliveryExampleFees + this.additionalPickupExampleFees;
    this.baseToBaseOrderExampleFees = feePerBaseToBaseDeliveryInDollars * multiplier;
  }
}
